import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import RoutePath from '../../route';
import '../style.scss';
import Loader from '../../common/loader/Loader';
import { COMMON } from '../../constants';
import configInstance from '../../configSingleton';

const pageTitleMap = {
    '/subscriber': 'Students - Saga',
    '/subscriber/details': 'Student Details - Saga',
    '/subscriber/add-subscriber': 'Add Student - Saga',
    '/subscriber/program': 'Program - Saga',
    '/admin': 'Admin - Saga',
    '/admin/details': 'Admin Student Details - Saga',
    '/content-library': 'Content Library - Saga',
    '/offers': 'Offers - Saga',
    '/offer': 'Offer Details - Saga',
    '/vendors': 'Vendor Management - Saga',
    '/vendor': 'Vendor Details - Saga',
    '/reporting': 'Reporting - Saga',
    '/lawReady': 'LawReady - Saga',
    '/pad': 'PAD - Saga',
    '/unauthorised': 'Unauthorised - Saga',
    '/licencee-mark': 'Official LSAT Content Licensee Mark - Saga'
};

const SubscriberHomeBody = lazy(
    () => import('../../modules/subscriber/containers/SubscriberHome')
);
const AdminHomeMainBody = lazy(
    () => import('../../modules/admin/containers/AdminHome')
);
const SubscriberDetailsBody = lazy(
    () => import('../../modules/subscriber/containers/SubscriberDetails')
);
const AdminSubscriberDetailsBody = lazy(
    () => import('../../modules/admin/containers/AdminDetails')
);
const ContentLibraryHome = lazy(
    () => import('../../modules/content-library/containers/ContentLibraryHome')
);
const AddSubscriber = lazy(
    () => import('../../modules/subscriber/containers/SubscriberHome')
);
const ReportingHome = lazy(
    () => import('../../modules/reporting/containers/ReportingHome')
);
const OffersHome = lazy(
    () => import('../../modules/offers/containers/OffersHome')
);
const OfferDetails = lazy(
    () => import('../../modules/offers/containers/OfferDetails')
);
const VendorManagementHome = lazy(
    () =>
        import(
            '../../modules/vendor-management/containers/VendorManagementHome'
        )
);
const VendorDetails = lazy(
    () => import('../../modules/vendor-management/containers/VendorDetails')
);
const ContentBuilderHome = lazy(
    () => import('../../modules/content-builder/containers/ContentBuilderHome')
);
const PageNotFound = lazy(
    () => import('../../common/page-not-found/PageNotFound')
);
const Unauthorised = lazy(
    () => import('../../common/Unauthorised/Unauthorised')
);
const StudentSummaryViewHome = lazy(
    () => import('../../modules/SummaryView/containers/StudentSummaryViewHome')
);
const LicenceeMark = lazy(
    () => import('../../modules/resources/containers/LicenseeMark')
);
const Body = React.memo((props) => {
    const config = configInstance.getConfig();
    const location = useLocation();

    useEffect(() => {
        const path = location.pathname;
        const matchedKey = Object.keys(pageTitleMap)
            .sort((a, b) => b.length - a.length)
            .find((key) => path.startsWith(key));
        document.title = matchedKey ? pageTitleMap[matchedKey] : 'Saga';
    }, [location.pathname]);

    const userRoles = props?.vendorRoleInfo?.userRoles;
    const hasAdminRole = userRoles?.some(
        (role) => role.vendorId === COMMON.admin
    );

    const canAccessAdmin =
        hasAdminRole && config.REACT_APP_FEATURE_ADMIN_ROLE === 'true';

    const ProtectedRoute = ({ children }) => {
        return canAccessAdmin ? (
            children
        ) : (
            <Navigate to={RoutePath.UNAUTHORISED} replace />
        );
    };

    return (
        <div className="body-content-wrap">
            <Suspense fallback={<Loader isLoading={true} />}>
                <Routes>
                    <Route
                        path={RoutePath.UNAUTHORISED}
                        element={<Unauthorised />}
                    />
                    {config.REACT_APP_PLUS_GUIDED_JOURNEY_FEATURE ===
                        'true' && (
                        <Route
                            path={`${RoutePath.PROGRAM}/:vendorId`}
                            element={<StudentSummaryViewHome />}
                        />
                    )}
                    <Route
                        path={`${RoutePath.PAD}/:vendorid`}
                        element={<StudentSummaryViewHome />}
                    />
                    <Route
                        path={`${RoutePath.SUBSCRIBER}/:vendorId`}
                        element={<SubscriberHomeBody />}
                    />
                    <Route
                        path="/"
                        element={
                            canAccessAdmin ? (
                                <Navigate to={RoutePath.ADMIN} replace />
                            ) : (
                                <Navigate to={RoutePath.SUBSCRIBER} replace />
                            )
                        }
                    />
                    <Route
                        path={`${RoutePath.SUBSCRIBER_DETAILS}/:vendorId/:coachingId`}
                        element={<SubscriberDetailsBody />}
                    />
                    <Route
                        path={RoutePath.SUBSCRIBER}
                        element={<SubscriberHomeBody />}
                    />
                    <Route
                        path={RoutePath.LICENCEE_MARK_WITH_VENDOR}
                        element={<LicenceeMark />}
                    />
                    <Route
                        path={`${RoutePath.CONTENT_LIBRARY}/:vendorId`}
                        element={<ContentLibraryHome />}
                    />
                    <Route
                        path={RoutePath.ADD_SUBSCRIBER}
                        element={<AddSubscriber />}
                    />
                    <Route
                        path={`${RoutePath.OFFERS}/:vendorId`}
                        element={<OffersHome />}
                    />
                    <Route
                        path={`${RoutePath.OFFER_DETAILS}/:vendorId/:offerId`}
                        element={<OfferDetails />}
                    />
                    {config.REACT_APP_VENDOR_USER_FEATURE === 'true' && (
                        <Route
                            path={`${RoutePath.VENDOR_DETAILS}/:vendorId${RoutePath.USER_DETAILS}`}
                            element={<VendorDetails />}
                        />
                    )}

                    <Route
                        path={`${RoutePath.LAWREADY}/:vendorId`}
                        element={<StudentSummaryViewHome />}
                    />
                    {/* Admin Protected Routes */}
                    <Route
                        path={RoutePath.ADMIN}
                        element={
                            <ProtectedRoute>
                                <AdminHomeMainBody />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={`${RoutePath.ALL_SUBSCRIBER_DETAILS}/:userId`}
                        element={
                            <ProtectedRoute>
                                <AdminSubscriberDetailsBody />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={RoutePath.VENDOR_MANAGEMENT}
                        element={
                            <ProtectedRoute>
                                <VendorManagementHome />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={`${RoutePath.VENDOR_DETAILS}/:vendorId`}
                        element={
                            <ProtectedRoute>
                                <VendorDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path={RoutePath.REPORTING}
                        element={
                            <ProtectedRoute>
                                <ReportingHome />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<PageNotFound />} />
                </Routes>
            </Suspense>
        </div>
    );
});

export default Body;
